import Dexie, { Table } from 'dexie';
import { ClearDataBase } from './interfaces/clearDataBase';
import {
  DeclarationViewer,
  FormEditedData,
  DeclarationContext,
  LocalBackupDeclaration,
  FormEditedDataDTO,
} from '90.quickConnect.Models/models';
import CustomLogger from '80.quickConnect.Core/logger/customLogger';
import { SynchronizeDeclaration } from '40.quickConnect.DataAccess/indexedDb/dbs/queriesTypes/declaration';
import { DocumentTransferState } from '90.quickConnect.Models/enums';
import { errorHandler } from '80.quickConnect.Core/helpers';

export class DeclarationsDb extends Dexie implements ClearDataBase {
  // Tag
  private static readonly TAG = '40.quickConnect.DataAccess/indexedDb/dbs/declarationsDb.ts';

  declarations!: Table<FormEditedData>;

  declarationsToUpload!: Table<SynchronizeDeclaration>;

  declarationListItems!: Table<DeclarationViewer>;

  // Sauvegardes automatiques des saisies en cours, une par déclaration
  backupDeclarations!: Table<LocalBackupDeclaration>;

  draftDeclarations!: Table<LocalBackupDeclaration>;

  logger: CustomLogger = CustomLogger.getInstance();

  // ATTENTION: Toute modification du schema doit être suivi d'une montée de version de la base de données afin de ne pas créer des crashs!! NE PAS CHANGER LES CLES PRIMAIRES DIRECTEMENT
  constructor() {
    super('declarationsDb');
    this.version(6).stores({
      declarations: 'id',
      declarationListItems: 'id',
      backupDeclarationContext: '[formId+userUPN], userUPN',
      draftDeclarations: 'id',
      declarationsToUpload: 'userUPN, customerId, [customerId+userUPN], [customerId+transferState]',
    });
    // Les sauvegardes automatiques ne sont plus uniques par formulaire mais par déclaration : la clé primaire ne pouvant
    // pas changer, elles sont recopiées dans une nouvelle table, puis l'ancienne est supprimée
    this.version(7)
      .stores({ backupDeclarations: 'id, userUPN' })
      .upgrade(async (transaction) => {
        const backups: LocalBackupDeclaration[] = await transaction.table('backupDeclarationContext').toArray();
        await transaction.table('backupDeclarations').bulkPut(backups.filter(({ id }) => !!id));
      });
    this.version(8).stores({ backupDeclarationContext: null });
  }

  countBackUpDclRecords = async (): Promise<number> => {
    try {
      return await this.backupDeclarations.count();
    } catch (error: unknown) {
      errorHandler(DeclarationsDb.TAG, error, 'countBackUpDclRecords');
      return 0;
    }
  };

  countDraftDclRecords = async (): Promise<number> => {
    try {
      return await this.draftDeclarations.count();
    } catch (error: unknown) {
      errorHandler(DeclarationsDb.TAG, error, 'countDraftDclRecords');
      return 0;
    }
  };

  countDclToUploadRecords = async (): Promise<number> => {
    try {
      return await this.declarationsToUpload.count();
    } catch (error: unknown) {
      errorHandler(DeclarationsDb.TAG, error, 'countDclToUploadRecords');

      return 0;
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  async clearAllTables(logger: CustomLogger) {}

  getDeclarationsToUpload = async (customerId: string): Promise<SynchronizeDeclaration[]> => {
    try {
      const records = this.declarationsToUpload
        .where({
          customerId: customerId,
          transferState: DocumentTransferState.ToBeTransfered,
        })
        .toArray();

      return records ? await records : [];
    } catch (error: unknown) {
      errorHandler(DeclarationsDb.TAG, error, 'getDeclarationsToUpload');
      return [];
    }
  };

  addDeclarationToUpload = async (request: SynchronizeDeclaration): Promise<boolean> => {
    try {
      const userUPNSyncDeclaration: SynchronizeDeclaration | undefined = await this.declarationsToUpload.get(
        request.userUPN,
      );

      if (userUPNSyncDeclaration) {
        const { editedData } = userUPNSyncDeclaration;
        const { editedData: editedDataFromRequest, customerId } = request;
        // Ignore duplicate declarations based on ID
        const existingIds = new Set(editedData.map((item) => item.id));
        const newEditedData: FormEditedDataDTO[] = [
          ...editedData,
          ...editedDataFromRequest.filter((item) => !existingIds.has(item.id)),
        ];
        const newData = { ...userUPNSyncDeclaration, editedData: newEditedData, customerId: customerId };
        await this.declarationsToUpload.put(newData, request.userUPN);
      } else {
        await this.declarationsToUpload.add(request, request.userUPN);
      }

      return true;
    } catch (error: unknown) {
      errorHandler(DeclarationsDb.TAG, error, 'addDeclarationToSend');
      return false;
    }
  };

  removeDeclarationByUserUPN = async (userUPN: string): Promise<boolean> => {
    try {
      await this.declarationsToUpload.delete(userUPN);

      return true;
    } catch (error: unknown) {
      errorHandler(DeclarationsDb.TAG, error, 'removeDeclarationByUserUPN');

      return false;
    }
  };

  removeProcessedDeclarations = async (userUPN: string, processedDeclarationIds: string[]): Promise<boolean> => {
    try {
      const userRecord = await this.declarationsToUpload.get(userUPN);

      if (!userRecord) {
        // No record found for user, nothing to remove
        return true;
      }

      // Filter out the processed declarations
      const remainingDeclarations = userRecord.editedData.filter(
        (declaration) => !processedDeclarationIds.includes(declaration.id),
      );

      if (remainingDeclarations.length === 0) {
        // All declarations were processed, remove the entire record
        await this.declarationsToUpload.delete(userUPN);
      } else {
        // Update the record with remaining declarations
        const updatedRecord = {
          ...userRecord,
          editedData: remainingDeclarations,
        };
        await this.declarationsToUpload.put(updatedRecord, userUPN);
      }

      return true;
    } catch (error: unknown) {
      errorHandler(DeclarationsDb.TAG, error, 'removeProcessedDeclarations');
      return false;
    }
  };

  /**
   *
   * @returns la déclaration à partir de son ID
   */
  public getDeclaration = async (declarationId: string): Promise<FormEditedData | undefined> => {
    try {
      const decl = await this.declarations.where({ id: declarationId }).first();
      return decl ? decl : undefined;
    } catch (error) {
      errorHandler(DeclarationsDb.TAG, error, 'getDeclaration');
    }
  };

  public getLocalDrafts = async (userUPN: string, logger: CustomLogger): Promise<DeclarationViewer[]> => {
    try {
      const formsEdited = await this.draftDeclarations.toArray();

      const formsEditedByCurrentUserUPN = formsEdited.filter(
        (draft: LocalBackupDeclaration) => draft.userUPN === userUPN,
      );

      const declarationsViewerWithUndefined = await Promise.all(
        formsEditedByCurrentUserUPN.map((form: LocalBackupDeclaration) => {
          return this.declarationListItems.where({ id: form.id }).first();
        }),
      );

      return declarationsViewerWithUndefined.filter(
        (item: DeclarationViewer | undefined): item is DeclarationViewer => {
          return !!item;
        },
      );
    } catch (error) {
      errorHandler(DeclarationsDb.TAG, error, 'getLocalDrafts');

      return [];
    }
  };

  public getLocalDraftByDeclarationId = async (declarationId: string): Promise<DeclarationContext | undefined> => {
    try {
      const localDraftDeclaration = await this.draftDeclarations.get({ id: declarationId });

      if (localDraftDeclaration) {
        const { userUPN, ...rest } = localDraftDeclaration;

        return rest;
      }
    } catch (error: unknown) {
      errorHandler(DeclarationsDb.TAG, error, 'getLocalDraftByDeclarationId');
    }
  };

  public insertBackUpDeclarationContext = async (
    context: DeclarationContext,
    userUPN: string,
    stringExtractDisplayed: string,
    logger: CustomLogger,
    inboxId?: string,
  ): Promise<void> => {
    try {
      const localBackupDeclarationContext: LocalBackupDeclaration = {
        ...context,
        userUPN,
        stringExtractDisplayed,
        inboxId,
      };

      await this.backupDeclarations.put(localBackupDeclarationContext);
    } catch (error) {
      errorHandler(DeclarationsDb.TAG, error, 'insertBackUpDeclarationContext');
    }
  };

  public getBackUpDeclarationContext = async (declarationId: string): Promise<LocalBackupDeclaration | undefined> => {
    try {
      return await this.backupDeclarations.get(declarationId);
    } catch (error) {
      errorHandler(DeclarationsDb.TAG, error, 'getBackUpDeclarationContext');
    }
  };

  /** Permet de supprimer un enrigistrement de la table des Déclarations */
  removeDeclaration = async (logger: CustomLogger, declarationId: string): Promise<number> => {
    try {
      return await this.declarations.where({ id: declarationId }).delete();
    } catch (error) {
      errorHandler(DeclarationsDb.TAG, error, 'removeDeclaration');
      return 0;
    }
  };

  removeDraftDeclarationById = async (declarationId: string): Promise<number> => {
    try {
      return await this.draftDeclarations.where({ id: declarationId }).delete();
    } catch (error: unknown) {
      errorHandler(DeclarationsDb.TAG, error, 'removeDraftDeclaration');

      return 0;
    }
  };

  /** Permet de supprimer la sauvegarde automatique d'une déclaration */
  removeBackupDeclaration = async (logger: CustomLogger, declarationId: string): Promise<number> => {
    try {
      return await this.backupDeclarations.where({ id: declarationId }).delete();
    } catch (error) {
      errorHandler(DeclarationsDb.TAG, error, 'removeDeclaration');

      return 0;
    }
  };

  /**
   *Permet de trouver les sauvegardes automatiques de l'utilisateur courant
   *
   * @param {CustomLogger} logger
   * @param {string} userUPN
   * @memberof DeclarationsDb
   */
  findBackUpByUserUPNAsync = async (logger: CustomLogger, userUPN: string): Promise<LocalBackupDeclaration[]> => {
    try {
      return await this.backupDeclarations.where({ userUPN }).toArray();
    } catch (error: unknown) {
      errorHandler(DeclarationsDb.TAG, error, 'findBackUpByUserUPN');

      return [];
    }
  };

  async deleteListItemsDeclaration(declarationId: string): Promise<boolean> {
    try {
      await this.declarationListItems.where('id').equals(declarationId).delete();

      return true;
    } catch (error) {
      errorHandler(DeclarationsDb.TAG, error, 'deleteListItemsDeclaration');
      return false;
    }
  }
}

const declarationsDb = new DeclarationsDb();

export default declarationsDb;
